// Same-origin API client for /v1/* endpoints.
//
// Per architecture §1 the LB serves both the SPA shell and /v1/* on the
// same hostname; per api-design §1 authentication is the HttpOnly
// `sowm_session` cookie set by the backend at /v1/auth/callback. The
// client therefore never reads or writes cookies directly — it just
// issues fetches with `credentials: 'include'`.
//
// Retry semantics (frontend-design §6 Retry semantics):
// - GET (read) errors: client may auto-retry up to 3 times (exponential
//   backoff) for 503 service_unavailable or transport-level network
//   errors only. Do NOT auto-retry 401, 403, 404, 422, 409.
// - POST (create/action) errors: NEVER auto-retry — V1 has no idempotency
//   key surface; auto-retry could create duplicates.
// - PATCH / DELETE: idempotent by content equivalence; auto-retry only
//   for 503 / transport network errors, up to 3 attempts.

import {
  ApiError,
  type ApiErrorBody,
  type ApiErrorCode,
} from '@/types/errors';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

interface RequestOptions {
  query?: Record<string, string | number | boolean | string[] | undefined | null>;
  body?: unknown;
  signal?: AbortSignal;
  // Only honored for read methods. POST and action endpoints never auto-retry.
  retryable?: boolean;
}

const DEFAULT_RETRYABLE_STATUS: ReadonlySet<number> = new Set([503]);
const MAX_RETRIES = 3;
const BASE_RETRY_DELAY_MS = 250;

function buildSearchParams(
  query: RequestOptions['query'],
): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const v of value) {
        if (v !== undefined && v !== null) params.append(key, String(v));
      }
    } else {
      params.append(key, String(value));
    }
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

async function parseError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    body = {
      error_code: pickCodeForStatus(res.status),
      message: `Request failed with status ${res.status}.`,
    };
  }
  if (!body || typeof body.error_code !== 'string') {
    body = {
      error_code: pickCodeForStatus(res.status),
      message:
        typeof (body as { message?: unknown })?.message === 'string'
          ? (body as { message: string }).message
          : `Request failed with status ${res.status}.`,
    };
  }
  return new ApiError(res.status, body);
}

function pickCodeForStatus(status: number): ApiErrorCode {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 503) return 'service_unavailable';
  if (status >= 500) return 'internal_error';
  return 'validation_failed';
}

// Subscribers for the global "we just got a 401 — handle re-auth"
// signal. The PrincipalContext registers one subscriber on mount.
type UnauthorizedHandler = () => void;
const unauthorizedHandlers = new Set<UnauthorizedHandler>();

export function onUnauthorized(handler: UnauthorizedHandler): () => void {
  unauthorizedHandlers.add(handler);
  return () => {
    unauthorizedHandlers.delete(handler);
  };
}

function fireUnauthorized(): void {
  for (const h of unauthorizedHandlers) {
    try {
      h();
    } catch {
      // Handler bugs must not break the failed request's caller.
    }
  }
}

async function attemptOnce(
  method: HttpMethod,
  url: string,
  body: unknown,
  signal: AbortSignal | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  let bodyInit: BodyInit | undefined;
  if (body !== undefined && method !== 'GET') {
    headers['Content-Type'] = 'application/json';
    bodyInit = JSON.stringify(body);
  }
  // V1 has NO client idempotency-key surface (api-design §1 Idempotency,
  // frontend-design §1). The frontend MUST NOT send Idempotency-Key.
  return fetch(url, {
    method,
    headers,
    body: bodyInit,
    credentials: 'include',
    signal,
  });
}

export async function request<T>(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  if (!path.startsWith('/v1/') && !path.startsWith('/v1')) {
    throw new Error(`API path must begin with /v1/: got ${path}`);
  }
  const url = `${path}${buildSearchParams(options.query)}`;
  const allowRetry =
    options.retryable !== false && (method === 'GET' || method === 'PATCH' || method === 'DELETE');
  let lastError: unknown = null;
  let lastResponse: Response | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      const res = await attemptOnce(method, url, options.body, options.signal);
      lastResponse = res;
      if (res.ok) {
        if (res.status === 204) {
          return undefined as T;
        }
        // Some endpoints return empty bodies even with 200; tolerate it.
        const text = await res.text();
        if (!text) {
          return undefined as T;
        }
        try {
          return JSON.parse(text) as T;
        } catch {
          throw new ApiError(res.status, {
            error_code: 'internal_error',
            message: 'Server returned a non-JSON response.',
          });
        }
      }

      // Non-2xx — parse the error envelope.
      const apiErr = await parseError(res);
      if (apiErr.status === 401) {
        // Fire the global unauthorized signal so the AppShell can
        // surface the re-auth modal. The caller still sees the error.
        fireUnauthorized();
      }
      const retryable = allowRetry && DEFAULT_RETRYABLE_STATUS.has(apiErr.status);
      if (retryable && attempt < MAX_RETRIES) {
        await sleep(BASE_RETRY_DELAY_MS * 2 ** attempt, options.signal);
        continue;
      }
      throw apiErr;
    } catch (err) {
      // Re-throw aborts immediately.
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err;
      }
      // ApiError: only loop if we explicitly chose to retry above.
      if (err instanceof ApiError) {
        lastError = err;
        // attemptOnce path threw a synthesized ApiError (non-JSON body
        // case). Don't loop on that.
        throw err;
      }
      // Network/transport error — TypeError from fetch typically.
      lastError = err;
      if (allowRetry && attempt < MAX_RETRIES) {
        await sleep(BASE_RETRY_DELAY_MS * 2 ** attempt, options.signal);
        continue;
      }
      throw new ApiError(0, {
        error_code: 'network_error',
        message:
          err instanceof Error
            ? `Network error: ${err.message}`
            : 'Network error: failed to reach server.',
      });
    }
  }
  // Loop exit without success.
  if (lastError instanceof ApiError) throw lastError;
  if (lastResponse) {
    throw new ApiError(lastResponse.status, {
      error_code: pickCodeForStatus(lastResponse.status),
      message: `Request failed with status ${lastResponse.status}.`,
    });
  }
  throw new ApiError(0, {
    error_code: 'network_error',
    message: 'Network error: failed to reach server.',
  });
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'body'>) =>
    request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body' | 'retryable'>) =>
    request<T>('POST', path, { ...options, body, retryable: false }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
    request<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'body'>) =>
    request<T>('DELETE', path, options),
};
